import * as React from "react";
import { formSet, formSetValue, useCpFormAnyValueId } from "../../gvc/testNewForm/utils";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ContentLayout } from "../../../components/layout/contentLayout";
import { SingleNavMenuLink, toLinkButton, toLinkPage, toLinkSpace } from "../../identity/useNavMenu";
import { useAppDispatch } from "../../../store/hooks";
import { setRedirect } from "../../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { store } from "../../../store";
import { PolicyInput } from "../../../schema/types/policy/input";
import { PermissionWithImplied } from "../../../schema/types/policy/entity";
import { homeLink, request } from "../../../services/cpln";
import { Permissions } from "../../../schema/types/permissions";
import { General } from "./general";
import { TargetKind } from "./targetKind";
import { TargetItems } from "../targetItems/targetItems";
import { TargetQuery } from "../targetQuery/targetQuery";
import { Bindings } from "../bindings";
import { CpRootError } from "../../gvc/testNewForm/CpRootError";

interface Props {
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const PolicyCreateContent: React.FC<Props> = ({ ignoreBlockerRef }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/policy/-create/*`);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);

  React.useEffect(() => {
    const startingPage = `${basePath}/general`;
    navigate(startingPage, { replace: true });
  }, []);

  const targetKind = useCpFormAnyValueId(formId, `values.targetKind`);

  React.useEffect(() => {
    fetchPermissions(targetKind);
  }, [targetKind]);

  async function fetchPermissions(targetKind: string) {
    if (!targetKind) {
      return;
    }

    const form = store.getState().form.instances[formId];
    const values: PolicyInput = form.values as any;

    const permissions = values.meta.permissionsWithImplied[targetKind];
    if (permissions) {
      return;
    }
    try {
      const { data } = await request<Permissions>({ url: `${homeLink(targetKind as any)}/-schema/permissions` });
      const permissionsWithImplied: PermissionWithImplied[] = [];
      for (const _permission of data.items) {
        const permission: PermissionWithImplied = {
          ..._permission,
          description: _permission.description || "",
          implied: data.implied?.[_permission.name] || [],
        };
        permissionsWithImplied.push(permission);
      }
      formSetValue(formId, `meta.permissionsWithImplied.${targetKind}`, permissionsWithImplied);
    } catch (e) {
      // TODO show error page here
    }
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Policy", "/general", { names: [`name`, `description`] }),
      toLinkPage("Target", "/target"),
      toLinkPage("Items", "/items"),
      toLinkPage("Query", "/query", { names: ["targetQuery"] }),
      toLinkPage("Bindings", "/bindings"),
      toLinkPage("Tags", "/tags", { names: ["tags"] }),
      toLinkSpace(),
      toLinkButton("Cancel", {
        type: "primary",
        onClick: () => {
          ignoreBlockerRef.current = true;
          const back = pathname.split(`/-create`)[0];
          dispatch(setRedirect(back));
        },
        danger: true,
        disabled: submitting,
      }),
      toLinkButton("Preview", {
        type: "default",
        onClick: () => formSet(formId, `meta.previewing`, true),
        disabled: submitting,
      }),
      toLinkButton("Create", {
        type: "primary",
        onClick: async () => {
          await submit();
        },
        className: "action",
        loading: submitting,
        disabled: submitting,
      }),
    );

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={false} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`general/*`} element={<General />} />
              <Route path={`target/*`} element={<TargetKind />} />
              <Route path={`items/*`} element={<TargetItems />} />
              <Route path={`query/*`} element={<TargetQuery />} />
              <Route path={`bindings/*`} element={<Bindings />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
